import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, X, Save, AlertCircle, Type, Hash, Calendar, 
  List, CheckSquare, FileText, Settings, Search, Filter, Upload
} from 'lucide-react';
import { customFieldsAPI } from '../../services/api';

// Field Type Options
const FIELD_TYPES = [
  { value: 'text', label: 'Text', icon: Type },
  { value: 'textarea', label: 'Text Area', icon: FileText },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'multiselect', label: 'Multi-Select', icon: CheckSquare },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  // Phase 18b.3 — single-choice radio group (UI alternative to select).
  { value: 'radio', label: 'Radio', icon: List },
  // Phase 18b — file uploads via /api/leads/{id}/custom-field-file.
  { value: 'file', label: 'File Upload', icon: Upload },
];

// Module Options
const MODULE_OPTIONS = [
  { value: 'leads', label: 'Leads' },
  { value: 'properties', label: 'Properties' },
  { value: 'lifecycle', label: 'Hotel Lifecycle' },
];

// Field Type Badge
const FieldTypeBadge = ({ type }) => {
  const fieldType = FIELD_TYPES.find(t => t.value === type);
  const Icon = fieldType?.icon || Type;
  
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
      <Icon size={12} />
      {fieldType?.label || type}
    </span>
  );
};

// Create/Edit Field Modal
const FieldModal = ({ isOpen, onClose, onSave, field, loading }) => {
  const [formData, setFormData] = useState({
    module: 'leads',
    field_name: '',
    field_label: '',
    field_type: 'text',
    is_required: false,
    options: [],
    default_value: '',
    placeholder: '',
    order: 0,
    is_active: true,
    section: 'Custom Fields',
    section_order: 0,
  });
  const [optionsText, setOptionsText] = useState('');
  
  useEffect(() => {
    if (field) {
      setFormData({
        module: field.module || 'leads',
        field_name: field.field_name || '',
        field_label: field.field_label || '',
        field_type: field.field_type || 'text',
        is_required: field.is_required || false,
        options: field.options || [],
        default_value: field.default_value || '',
        placeholder: field.placeholder || '',
        order: field.order || 0,
        is_active: field.is_active !== false,
        section: field.section || 'Custom Fields',
        section_order: field.section_order || 0,
      });
      setOptionsText((field.options || []).join('\n'));
    } else {
      setFormData({
        module: 'leads',
        field_name: '',
        field_label: '',
        field_type: 'text',
        is_required: false,
        options: [],
        default_value: '',
        placeholder: '',
        order: 0,
        is_active: true,
        section: 'Custom Fields',
        section_order: 0,
      });
      setOptionsText('');
    }
  }, [field, isOpen]);
  
  if (!isOpen) return null;
  
  const handleLabelChange = (value) => {
    setFormData(prev => ({
      ...prev,
      field_label: value,
      // Auto-generate field_name from label (if not editing)
      field_name: field ? prev.field_name : value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/g, '')
    }));
  };
  
  const handleOptionsChange = (text) => {
    setOptionsText(text);
    const options = text.split('\n').map(o => o.trim()).filter(o => o);
    setFormData(prev => ({ ...prev, options }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.field_label || !formData.field_name) {
      alert('Please fill in all required fields');
      return;
    }
    onSave(formData);
  };
  
  const needsOptions = ['select', 'multiselect'].includes(formData.field_type);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">
            {field ? 'Edit Custom Field' : 'Add Custom Field'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full"
            data-testid="close-field-modal"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Module <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.module}
                onChange={(e) => setFormData({ ...formData, module: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                disabled={!!field}
                data-testid="field-module-select"
              >
                {MODULE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Field Type <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.field_type}
                onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
                data-testid="field-type-select"
              >
                {FIELD_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Field Label <span className="text-red-500">*</span>
            </label>
            <input
              required
              type="text"
              value={formData.field_label}
              onChange={(e) => handleLabelChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="e.g. Company Size"
              data-testid="field-label-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Field Name <span className="text-red-500">*</span>
            </label>
            <input
              required
              type="text"
              value={formData.field_name}
              onChange={(e) => setFormData({ ...formData, field_name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none font-mono text-sm"
              placeholder="e.g. company_size"
              disabled={!!field}
              data-testid="field-name-input"
            />
            <p className="text-xs text-gray-500 mt-1">Unique identifier (lowercase, underscores only)</p>
          </div>
          
          {needsOptions && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Options <span className="text-red-500">*</span>
              </label>
              <textarea
                value={optionsText}
                onChange={(e) => handleOptionsChange(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none resize-none"
                placeholder="Enter each option on a new line"
                data-testid="field-options-input"
              />
              <p className="text-xs text-gray-500 mt-1">One option per line</p>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Placeholder</label>
            <input
              type="text"
              value={formData.placeholder}
              onChange={(e) => setFormData({ ...formData, placeholder: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="e.g. Enter company size..."
              data-testid="field-placeholder-input"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Value</label>
              <input
                type="text"
                value={formData.default_value}
                onChange={(e) => setFormData({ ...formData, default_value: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                placeholder="Optional default"
                data-testid="field-default-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                min="0"
                data-testid="field-order-input"
              />
            </div>
          </div>
          
          {/* Phase 18b — section grouping. Fields with the same section
              render under one collapsible group inside the Add/Edit Account
              popup. Section ordering controls vertical layout of sections. */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <input
                type="text"
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                placeholder="e.g. Custom Fields"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="field-section-input"
              />
              <p className="text-xs text-gray-500 mt-1">Fields sharing a section name are grouped together.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Order</label>
              <input
                type="number"
                value={formData.section_order}
                onChange={(e) => setFormData({ ...formData, section_order: parseInt(e.target.value) || 0 })}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                data-testid="field-section-order-input"
              />
            </div>
          </div>
          
          <div className="flex items-center gap-6 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                data-testid="field-required-checkbox"
              />
              <span className="text-sm text-gray-700">Required field</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                data-testid="field-active-checkbox"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>
        </form>
        
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg font-medium"
            data-testid="cancel-field-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium flex items-center disabled:opacity-50"
            data-testid="save-field-btn"
          >
            <Save size={18} className="mr-2" />
            {loading ? 'Saving...' : (field ? 'Update Field' : 'Create Field')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteModal = ({ isOpen, onClose, onConfirm, fieldLabel, loading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-6 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="text-red-600" size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Custom Field</h3>
          <p className="text-gray-600 mb-6">
            Are you sure you want to delete <span className="font-medium">"{fieldLabel}"</span>? 
            Existing data using this field will not be deleted but the field will no longer appear in forms.
          </p>
          <div className="flex justify-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium"
              data-testid="cancel-delete-field-btn"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center disabled:opacity-50"
              data-testid="confirm-delete-field-btn"
            >
              <Trash2 size={18} className="mr-2" />
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const CustomFieldsManagement = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldModal, setFieldModal] = useState({ open: false, field: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, field: null });
  const [filterModule, setFilterModule] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchFields();
  }, [filterModule]);

  const fetchFields = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await customFieldsAPI.getAll(filterModule || undefined);
      setFields(response.data?.data || []);
    } catch (err) {
      console.error('Error fetching fields:', err);
      setError('Failed to load custom fields. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveField = async (fieldData) => {
    try {
      setActionLoading(true);
      if (fieldModal.field) {
        // Update existing field
        await customFieldsAPI.update(fieldModal.field.id, fieldData);
      } else {
        // Create new field
        await customFieldsAPI.create(fieldData);
      }
      setFieldModal({ open: false, field: null });
      fetchFields();
    } catch (err) {
      console.error('Error saving field:', err);
      const detail = err.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : (detail?.message || detail?.msg || 'Failed to save field. Please try again.');
      alert(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteField = async () => {
    if (!deleteModal.field) return;
    
    try {
      setActionLoading(true);
      await customFieldsAPI.delete(deleteModal.field.id);
      setDeleteModal({ open: false, field: null });
      fetchFields();
    } catch (err) {
      console.error('Error deleting field:', err);
      alert('Failed to delete field. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredFields = fields.filter(field => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return field.field_label.toLowerCase().includes(term) || 
             field.field_name.toLowerCase().includes(term);
    }
    return true;
  });

  const groupedFields = filteredFields.reduce((acc, field) => {
    const module = field.module;
    if (!acc[module]) acc[module] = [];
    acc[module].push(field);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="text-red-500 mx-auto mb-3" size={32} />
        <p className="text-red-700 mb-4">{error}</p>
        <button 
          onClick={fetchFields}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-fields-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Custom Fields</h1>
          <p className="text-gray-500 mt-1">
            Create and manage custom fields for different modules.
          </p>
        </div>
        
        <button
          onClick={() => setFieldModal({ open: true, field: null })}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 shadow-sm"
          data-testid="add-custom-field-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Custom Field
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-xs">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search fields..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            data-testid="search-fields-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-gray-400" />
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-white"
            data-testid="filter-module-select"
          >
            <option value="">All Modules</option>
            {MODULE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="text-sm text-gray-500">
              <span className="font-semibold text-gray-800">{filteredFields.length}</span> fields
            </span>
            {Object.entries(groupedFields).map(([module, moduleFields]) => (
              <span key={module} className="text-sm text-gray-500">
                {MODULE_OPTIONS.find(m => m.value === module)?.label}: 
                <span className="font-semibold text-gray-800 ml-1">{moduleFields.length}</span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Fields by Module */}
      {Object.entries(groupedFields).map(([module, moduleFields]) => (
        <div key={module} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">
              {MODULE_OPTIONS.find(m => m.value === module)?.label || module}
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Label</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Field Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Required</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {moduleFields.map((field) => (
                  <tr key={field.id} className="hover:bg-gray-50" data-testid={`field-row-${field.id}`}>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-800">{field.field_label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-600">
                        {field.field_name}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <FieldTypeBadge type={field.field_type} />
                    </td>
                    <td className="px-4 py-3">
                      {field.is_required ? (
                        <span className="text-red-600 text-sm font-medium">Yes</span>
                      ) : (
                        <span className="text-gray-400 text-sm">No</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        field.is_active 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {field.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-1">
                        <button
                          onClick={() => setFieldModal({ open: true, field })}
                          className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg"
                          title="Edit field"
                          data-testid={`edit-field-${field.id}`}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteModal({ open: true, field })}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          title="Delete field"
                          data-testid={`delete-field-${field.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {filteredFields.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Settings className="text-gray-300 mx-auto mb-3" size={48} />
          <h4 className="text-lg font-medium text-gray-600 mb-1">No custom fields</h4>
          <p className="text-gray-500 mb-4">
            {searchTerm || filterModule 
              ? 'No fields match your search criteria.' 
              : 'Create your first custom field to extend the CRM.'}
          </p>
          {!searchTerm && !filterModule && (
            <button
              onClick={() => setFieldModal({ open: true, field: null })}
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <Plus size={18} className="mr-2" />
              Add Custom Field
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      <FieldModal
        isOpen={fieldModal.open}
        onClose={() => setFieldModal({ open: false, field: null })}
        onSave={handleSaveField}
        field={fieldModal.field}
        loading={actionLoading}
      />
      
      <DeleteModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, field: null })}
        onConfirm={handleDeleteField}
        fieldLabel={deleteModal.field?.field_label}
        loading={actionLoading}
      />
    </div>
  );
};

export default CustomFieldsManagement;
